import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { Compromisso } from '../../db/tipos'
import { dataISOLocal } from '../../pedidos/precos'
import { normalizarTexto } from '../../lib/textoBR'
import { confirmar } from '../../componentes/confirmar'
import { registrarGuardaDeSaida } from '../../componentes/guardaDeSaida'
import { LIMITE_ASSUNTO, nomeDoCliente, validarCompromisso, type RascunhoCompromisso } from '../../compromissos/regras'
import { alternarSituacao, excluirCompromisso, salvarCompromisso } from '../../compromissos/gravar'
import { AVISO_PADRAO, OPCOES_AVISO } from '../../compromissos/aviso'

// Spec 2026-10-02 (ciclo 2A), item 2: novo (/compromissos/novo?data=) e editar
// (/compromissos/:id). O formulario so desenha; regras e gravacao moram em
// compromissos/.
export default function FormCompromisso() {
  const { id } = useParams()
  const [parametros] = useSearchParams()
  const navegar = useNavigate()
  // undefined = carregando; null = nao existe (ou e novo).
  const existente = useLiveQuery(
    async () => (id ? (await banco.compromissos.get(id)) ?? null : null),
    [id],
  )
  const clientes = useLiveQuery(() => banco.clientes.toArray(), []) ?? []

  const [rascunho, setRascunho] = useState<RascunhoCompromisso | null>(null)
  const [busca, setBusca] = useState('')
  // Teste dela 04/10, item 3: tocar no campo ja abre a lista inteira.
  const [listaAberta, setListaAberta] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  // Revisao final: toque duplo no Salvar criava DOIS compromissos (cada toque
  // nascia com id novo). Trava os botoes enquanto grava.
  const [salvando, setSalvando] = useState(false)

  // Monta o rascunho UMA vez, quando o registro chega (nao e leitura de banco:
  // o dado vem do useLiveQuery acima).
  useEffect(() => {
    if (rascunho !== null || existente === undefined) return
    setRascunho(existente
      ? { data: existente.data, hora: existente.hora, assunto: existente.assunto, cliente_id: existente.cliente_id, aviso_minutos: existente.aviso_minutos ?? null }
      : { data: parametros.get('data') ?? dataISOLocal(new Date()), hora: '', assunto: '', cliente_id: null, aviso_minutos: AVISO_PADRAO })
  }, [existente, rascunho, parametros])

  const original: RascunhoCompromisso | null = existente
    ? { data: existente.data, hora: existente.hora, assunto: existente.assunto, cliente_id: existente.cliente_id, aviso_minutos: existente.aviso_minutos ?? null }
    : null
  const sujo = rascunho !== null && JSON.stringify(rascunho) !== JSON.stringify(original ?? { ...rascunho, assunto: '', hora: '' })

  useEffect(() => {
    if (!sujo) return
    return registrarGuardaDeSaida(() => confirmar('Descartar as alterações não salvas?', { botao: 'Descartar', perigo: true }))
  }, [sujo])

  if (id && existente === null) return <p className="p-4 text-slate-500">Compromisso não encontrado.</p>
  if (rascunho === null) return <p className="p-4 text-slate-500">Carregando…</p>
  // Item 7: o excluido fica cinza na lista e nao volta a valer.
  if (existente?.apagado_em) return <p className="p-4 text-slate-500">Este compromisso foi excluído.</p>

  const voltarAoDia = (data: string) => navegar(`/compromissos?data=${data}`, { replace: true })
  const mudar = (parte: Partial<RascunhoCompromisso>) => { setErro(null); setRascunho({ ...rascunho, ...parte }) }
  const termo = normalizarTexto(busca)
  const achados = !listaAberta ? [] : clientes
    .filter((c) => c.apagado_em === null && normalizarTexto(`${c.nome_fantasia ?? ''} ${c.razao_social}`).includes(termo))
    .sort((a, b) => (a.nome_fantasia || a.razao_social).localeCompare(b.nome_fantasia || b.razao_social, 'pt-BR'))
  const clienteEscolhido = nomeDoCliente(clientes, rascunho.cliente_id)

  async function salvar() {
    const problema = validarCompromisso(rascunho!)
    if (problema) { setErro(problema); return }
    setSalvando(true)
    try {
      await salvarCompromisso(rascunho!, existente ?? undefined)
      voltarAoDia(rascunho!.data)
    } finally {
      setSalvando(false)
    }
  }
  async function alternar(c: Compromisso) {
    setSalvando(true)
    try { await alternarSituacao(c) } finally { setSalvando(false) }
  }
  async function excluir(c: Compromisso) {
    if (!(await confirmar('Excluir este compromisso?', { botao: 'Excluir', perigo: true }))) return
    await excluirCompromisso(c.id)
    voltarAoDia(c.data)
  }

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">{existente ? 'Compromisso' : 'Novo compromisso'}</h2>
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-700">Data
            <input type="date" value={rascunho.data} onChange={(e) => mudar({ data: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2" />
          </label>
          <label className="text-sm text-slate-700">Hora
            <input type="time" value={rascunho.hora} onChange={(e) => mudar({ hora: e.target.value })}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2" />
          </label>
        </div>
        {/* Pedido dela 08/10 (D3): ja vem em 15 min ao criar. O aviso aparece
            com o app aberto (D1 a). */}
        <label className="block text-sm text-slate-700">Avisar com antecedência
          <select
            aria-label="Avisar com antecedência"
            value={rascunho.aviso_minutos ?? ''}
            onChange={(e) => mudar({ aviso_minutos: e.target.value === '' ? null : Number(e.target.value) })}
            className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 bg-white"
          >
            {OPCOES_AVISO.map((o) => (
              <option key={o.rotulo} value={o.minutos ?? ''}>{o.rotulo}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-slate-700">Assunto
          <input value={rascunho.assunto} maxLength={LIMITE_ASSUNTO} onChange={(e) => mudar({ assunto: e.target.value })}
            placeholder="Ex.: Retorno de proposta" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        <div className="text-sm text-slate-700 space-y-1">
          <span>Cliente (opcional)</span>
          {rascunho.cliente_id ? (
            <p className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span data-testid="cliente-escolhido">{clienteEscolhido ?? 'Cliente removido'}</span>
              <button type="button" onClick={() => mudar({ cliente_id: null })} className="text-teal-700 text-sm">Trocar</button>
            </p>
          ) : (
            <>
              <input value={busca} onChange={(e) => { setBusca(e.target.value); setListaAberta(true) }}
                onFocus={() => setListaAberta(true)} placeholder="Toque para escolher o cliente" data-testid="busca-cliente"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
              {achados.length > 0 && (
                <ul data-testid="lista-clientes" className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {achados.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => { setBusca(''); setListaAberta(false); mudar({ cliente_id: c.id }) }}
                        className="w-full text-left px-3 py-2">{c.nome_fantasia || c.razao_social}</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        {erro && <p role="alert" className="text-sm text-red-700">{erro}</p>}
        <button type="button" onClick={() => void salvar()} disabled={salvando} className="w-full rounded-lg bg-teal-700 text-white py-2 font-semibold">
          Salvar
        </button>
      </div>
      {existente && (
        <div className="space-y-2">
          <button type="button" onClick={() => void alternar(existente)} disabled={salvando} data-testid="alternar-situacao"
            className="w-full rounded-lg border border-teal-700 text-teal-800 py-2 font-semibold">
            {existente.situacao === 'pendente' ? 'Marcar como feito' : 'Voltar para pendente'}
          </button>
          <button type="button" onClick={() => void excluir(existente)} className="w-full rounded-lg text-red-700 py-2">
            Excluir
          </button>
        </div>
      )}
    </section>
  )
}
